import { SCHOOL_GROUPS_PATH, userPackagesActive, userSchoolsPath } from "../admin/paths";
import { PACKAGE_STATUS_PAID } from "../api/constants";
import { toDateFromServer } from "../api/dates";
import { dbKeyMap, domainMap } from "../api/dbConfig";
import type { MealPackage, UserMealPackage } from "../types";

/**
 * Calculate the duration of a package in days
 */
export function calculatePackageDuration(packageData: MealPackage): number {
    if (!packageData.start || !packageData.end) return 0;

    const startDate = toDateFromServer(packageData.start);
    const endDate = toDateFromServer(packageData.end);

    const diffTime = endDate.getTime() - startDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
}

/**
 * Check if package2 can get discount based on package1 duration
 * package1 is the paid package, package2 is the new package being tested
 * package2 can get discount if it has less duration than (package1 + 20% of package1)
 */
export function hasSimilarDuration(package1: MealPackage, package2: MealPackage): boolean {
    const duration1 = calculatePackageDuration(package1);
    const duration2 = calculatePackageDuration(package2);

    if (duration1 === 0 || duration2 === 0) return false;

    const maxAllowedDuration = duration1 + (duration1 * 0.2); // package1 + 20% of package1

    return duration2 <= maxAllowedDuration;
}

export type CrossSchoolSiblingInfo = {
    hasSiblingInOtherSchool: boolean;
    siblingSchoolKey: string;
    siblingPackage: UserMealPackage;
};

/**
 * Shared async check for cross-school sibling discount eligibility.
 * Requires a database getter compatible with both web and admin SDKs.
 */
export async function checkCrossSchoolSiblingDiscount(
    currentSchoolKey: string,
    currentPackage: MealPackage,
    uid: string,
    getDatabaseByUrl: (url: string) => any
): Promise<CrossSchoolSiblingInfo | null> {
    if (!currentSchoolKey || !currentPackage || !uid) return null;

    // Read user schools and school groups from MAIN database
    const mainDb = getDatabaseByUrl(domainMap.default.databaseURL);
    const userSchoolsSnap = await mainDb.ref(userSchoolsPath(uid)).once("value");
    const userSchoolsData = userSchoolsSnap?.val();
    if (!userSchoolsData) return null;

    const schoolGroupsSnap = await mainDb.ref(SCHOOL_GROUPS_PATH).once("value");
    const schoolGroups = schoolGroupsSnap?.val();
    if (!schoolGroups) return null;

    let currentGroupKey: string | null = null;
    for (const [groupKey, schools] of Object.entries(schoolGroups)) {
        if ((schools as any)[currentSchoolKey]) {
            currentGroupKey = groupKey;
            break;
        }
    }
    if (!currentGroupKey) return null;

    const userSchoolKeys = Object.keys(userSchoolsData).filter((key) => userSchoolsData[key] === true);
    const groupSchools = Object.keys((schoolGroups as any)[currentGroupKey] || {});
    const relevantSchoolKeys = groupSchools.filter((key) => key !== currentSchoolKey && userSchoolKeys.includes(key));
    if (relevantSchoolKeys.length === 0) return null;

    for (const schoolKey of relevantSchoolKeys) {
        try {
            const domainKey = dbKeyMap[schoolKey];
            const dbCfg = domainMap[domainKey] || domainMap.default;
            const db = getDatabaseByUrl(dbCfg.databaseURL);
            const otherActiveSnap = await db.ref(userPackagesActive(uid)).once("value");
            const otherActive = (otherActiveSnap?.val() || {}) as Record<string, UserMealPackage>;
            const paid = Object.values(otherActive).filter((p) => p.status >= PACKAGE_STATUS_PAID);
            for (const pkg of paid) {
                if (hasSimilarDuration(currentPackage, { start: pkg.start, end: pkg.end } as MealPackage)) {
                    return {
                        hasSiblingInOtherSchool: true,
                        siblingSchoolKey: schoolKey,
                        siblingPackage: pkg,
                    };
                }
            }
        } catch {
            // ignore and continue
        }
    }

    return null;
}


